import React, { useEffect, useState, useMemo } from 'react'

import HttpClient from '../../services/HttpClient'
import HomeHeader from '../../components/HomeHeader/HomeHeader'
import HomeQuickNav from '../../components/Home/HomeQuickNav'
import HomeStatsPanel from '../../components/Home/HomeStatsPanel'
import HomeFeaturedTerritories from '../../components/Home/HomeFeaturedTerritories'
import HomeActiveAdventures from '../../components/Home/HomeActiveAdventures'
import HomeWorldMap from '../../components/Home/HomeWorldMap'
import FeaturedAdventureCard from '../../components/AdventureList/FeaturedAdventureCard'
import TerritoryGrid from '../../components/TerritoryGrid/TerritoryGrid'
import TerritoryFilters from '../../components/TerritoryFilters/TerritoryFilters'

import './Home.css'

function parseDate(s) {
  try {
    return s ? new Date(s) : new Date(0)
  } catch {
    return new Date(0)
  }
}

function sortAdventuresByRecent(list) {
  return [...list].sort(
    (a, b) => parseDate(b.updated_at || b.created_at) - parseDate(a.updated_at || a.created_at)
  )
}

export default function Home() {
  const [territories, setTerritories] = useState([])
  const [filteredTerritories, setFilteredTerritories] = useState([])
  const [filterStats, setFilterStats] = useState(null)
  const [globalStats, setGlobalStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const [adventures, setAdventures] = useState([])
  const [adventuresLoading, setAdventuresLoading] = useState(true)
  const [adventuresError, setAdventuresError] = useState(null)
  const [territoriesError, setTerritoriesError] = useState(null)

  useEffect(() => {
    document.title = "Shinobis' War :: Welcome!"
    loadTerritories()
    loadGlobalStats()
    loadAdventures()
  }, [])

  const loadTerritories = async () => {
    setTerritoriesError(null)
    try {
      const { data } = await HttpClient().get('/api/v1/stats/territories-with-stats')
      const list = Array.isArray(data) ? data : []
      setTerritories(list)
      setFilteredTerritories(list)
    } catch (error) {
      console.error('Error fetching territories with stats:', error)
      try {
        const { data } = await HttpClient().get('/api/v1/territory')
        const list = Array.isArray(data) ? data : []
        setTerritories(list)
        setFilteredTerritories(list)
      } catch (fallbackError) {
        console.error('Error fetching territories:', fallbackError)
        setTerritoriesError('No se pudieron cargar los territorios.')
        setTerritories([])
        setFilteredTerritories([])
      }
    }
  }

  const loadGlobalStats = async () => {
    setStatsLoading(true)
    try {
      const { data } = await HttpClient().get('/api/v1/stats/global')
      setGlobalStats(data)
    } catch (error) {
      console.error('Error fetching global stats:', error)
      setGlobalStats(null)
    } finally {
      setStatsLoading(false)
    }
  }

  const loadAdventures = async () => {
    setAdventuresLoading(true)
    setAdventuresError(null)
    try {
      const { data } = await HttpClient().get('/api/v1/adventure/all/enhanced')
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []
      setAdventures(list)
    } catch (error) {
      console.error('Error fetching adventures:', error)
      setAdventuresError('No se pudo cargar la actividad reciente.')
      setAdventures([])
    } finally {
      setAdventuresLoading(false)
    }
  }

  const sortedAdventures = useMemo(() => sortAdventuresByRecent(adventures), [adventures])
  const featuredAdventure = sortedAdventures[0] || null

  return (
    <div className="home-bento">
      {/* ── Banner principal ──────────────────────────────── */}
      <section className="home-bento__hero">
        <HomeHeader stats={globalStats} loading={statsLoading} />
      </section>

      {/* ── Tablón de misiones ────────────────────────────── */}
      <section className="home-bento__quick-nav">
        <HomeQuickNav />
      </section>

      {/* ── Grid bento central ───────────────────────────── */}
      <div className="home-bento__grid">
        <section className="home-bento__featured-adventure home-bento-card home-bento-card--featured">
          <header className="home-panel__header">
        <span className="home-panel__eyebrow">戦 · Destacado</span>
            <h2 className="home-panel__title">Crónica del momento</h2>
          </header>
          {adventuresLoading && <p className="home-panel__empty">Consultando los archivos de batalla…</p>}
          {!adventuresLoading && adventuresError && <p className="home-panel__empty">{adventuresError}</p>}
          {!adventuresLoading && !adventuresError && !featuredAdventure && (
            <p className="home-panel__empty">Las crónicas aún están vacías.</p>
          )}
          {!adventuresLoading && featuredAdventure && <FeaturedAdventureCard adventure={featuredAdventure} />}
        </section>

        <aside className="home-bento__sidebar">
          <HomeStatsPanel stats={globalStats} loading={statsLoading} territories={territories} />
        </aside>

        <section className="home-bento__top-territories">
          <HomeFeaturedTerritories territories={territories} />
          <HomeActiveAdventures
            adventures={adventures}
            loading={adventuresLoading}
            error={adventuresError}
            limit={3}
          />
        </section>
      </div>

      {/* ── Mapa del mundo ────────────────────────────────── */}
      <section className="home-bento__worldmap">
        <HomeWorldMap stats={globalStats} territories={territories} />
      </section>

      {/* ── Biblioteca de reinos (explorador) ────────────── */}
      <section id="home-explorer" className="home-bento__explorer">
        <header className="home-explorer__header">
          <span className="home-panel__eyebrow">地 · Explorar</span>
          <h2 className="home-explorer__title">Tierras conocidas</h2>
          {filterStats && territories.length > 0 && (
            <p className="home-explorer__meta">
              Mostrando {filterStats.totalResults} de {territories.length} territorios
              {filterStats.searchTerm ? ` · Búsqueda: "${filterStats.searchTerm}"` : ''}
            </p>
          )}
        </header>

        {territoriesError && (
          <div className="home-empty-state">
            <h3>Error al cargar territorios</h3>
            <p>{territoriesError}</p>
          </div>
        )}

        {!territoriesError && territories.length === 0 && (
          <div className="home-empty-state">
            <h3>No hay territorios disponibles</h3>
            <p>Parece que aún no se han creado territorios en el foro.</p>
          </div>
        )}

        {!territoriesError && territories.length > 0 && (
          <>
            <TerritoryFilters
              territories={territories}
              onFilteredTerritories={setFilteredTerritories}
              onFiltersChange={setFilterStats}
            />

            {filteredTerritories.length > 0 ? (
              <TerritoryGrid territories={filteredTerritories} />
            ) : (
              <div className="home-empty-state">
                <h3>No se encontraron territorios</h3>
                <p>No hay territorios que coincidan con los filtros aplicados.</p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
